// Renderer transport: the only surface the renderer uses to reach the compath BFF.
// See docs/http-contract.md.
import type { AppSummary, Principal, SDUIDocument } from "../model/types";

export interface Transport {
  me(): Promise<Principal>;
  apps(): Promise<AppSummary[]>;
  sdui(appId: string, screen: string, params?: Record<string, unknown>): Promise<SDUIDocument>;
  fetchData(appId: string, endpoint: string, opts: { method?: string; params?: Record<string, unknown>; body?: unknown }): Promise<unknown>;
  submit(appId: string, endpoint: string, opts: { method?: string; body?: unknown }): Promise<unknown>;
}

export class NotEntitledError extends Error {
  constructor(public app: string, public purchaseUrl?: string) {
    super(`not entitled: ${app}`);
    this.name = "NotEntitledError";
  }
}

function qs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    usp.set(k, typeof v === "object" ? JSON.stringify(v) : String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

/** Talks to the compath BFF at `${base}/api`. */
export class HttpTransport implements Transport {
  constructor(private base = "", private fetchImpl: typeof fetch = fetch) {}

  private async json(path: string, init?: RequestInit): Promise<unknown> {
    const res = await this.fetchImpl(`${this.base}/api${path}`, {
      credentials: "include",
      headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
    if (res.status === 403) {
      let body: any = {};
      try { body = await res.json(); } catch { /* ignore */ }
      if (body?.error === "not_entitled") throw new NotEntitledError(body.app, body.purchaseUrl);
    }
    if (!res.ok) throw new Error(`compath ${path} → ${res.status}`);
    return res.json();
  }

  me() { return this.json("/me") as Promise<Principal>; }
  apps() { return this.json("/apps") as Promise<AppSummary[]>; }
  sdui(appId: string, screen: string, params?: Record<string, unknown>) {
    return this.json(`/apps/${appId}/sdui/${screen}${qs(params)}`) as Promise<SDUIDocument>;
  }
  fetchData(appId: string, endpoint: string, opts: { method?: string; params?: Record<string, unknown>; body?: unknown }) {
    const method = opts.method ?? "GET";
    const path = `/apps/${appId}/proxy${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;
    if (method === "GET") return this.json(`${path}${qs(opts.params)}`);
    return this.json(path, { method, body: JSON.stringify(opts.body ?? opts.params ?? {}) });
  }
  submit(appId: string, endpoint: string, opts: { method?: string; body?: unknown }) {
    const path = `/apps/${appId}/proxy${endpoint.startsWith("/") ? "" : "/"}${endpoint}`;
    return this.json(path, { method: opts.method ?? "POST", body: JSON.stringify(opts.body ?? {}) });
  }
}

/** In-memory transport for tests / demo / playground. */
export class MockTransport implements Transport {
  constructor(
    private routes: {
      me?: Principal;
      apps?: AppSummary[];
      sdui?: Record<string, SDUIDocument>;
      data?: Record<string, unknown>;
      submit?: (appId: string, endpoint: string, body: unknown) => unknown;
    } = {},
  ) {}
  async me() { return this.routes.me ?? { id: "u_mock", name: "Mock", groups: [] }; }
  async apps() { return this.routes.apps ?? []; }
  async sdui(appId: string, screen: string) {
    const d = this.routes.sdui?.[`${appId}/${screen}`] ?? this.routes.sdui?.[screen];
    if (!d) throw new Error(`no mock sdui for ${appId}/${screen}`);
    return d;
  }
  async fetchData(appId: string, endpoint: string) {
    return this.routes.data?.[`${appId}${endpoint}`] ?? this.routes.data?.[endpoint] ?? [];
  }
  async submit(appId: string, endpoint: string, opts: { body?: unknown }) {
    return this.routes.submit?.(appId, endpoint, opts.body) ?? { ok: true };
  }
}
